import React, { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { X, Search, Plus, Minus, Edit2, ShoppingBag, Package, Trash2 } from 'lucide-react';
import EditPriceModal from './EditPriceModal';
import itemService from '../../services/itemService';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const parsePrice = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return isNaN(val) ? 0 : val;
  const cleanStr = val.toString().replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleanStr);
  return isNaN(parsed) ? 0 : parsed;
};

const ItemSelectModal = ({ isOpen, onClose, initialSelectedItems = [], onSelect, billType, isGstBill }) => {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState('product'); // 'product' or 'service'
  const [searchQuery, setSearchQuery] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // { id: { item, qty, price, ... } }
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const normalizeSelectedItemsKeys = (currentSelected, products) => {
    const normalized = {};
    Object.entries(currentSelected).forEach(([key, si]) => {
      const product = products.find(p => 
        p.id?.toString() === key || 
        p.id?.toString() === si.item?.id?.toString() || 
        p.id?.toString() === si.item_id?.toString() ||
        p.name?.toLowerCase() === (si.item?.name || si.name || '').toLowerCase()
      );
      
      const productId = product ? product.id : key;
      const nestedItem = si.item || product || {
        id: productId,
        name: si.name || si.item_name || product?.name || 'N/A',
        gstRate: si.gstRate || product?.gstRate || product?.gst_rate || '0%',
        gst_rate: si.gst_rate || product?.gst_rate || product?.gstRate || '0',
        taxIncluded: si.taxIncluded || product?.taxIncluded || false,
        sale_price: si.originalPrice || product?.sale_price || 0,
        purchase_price: si.originalPrice || product?.purchase_price || 0
      };
      const originalPrice = parsePrice(si.originalPrice) || parsePrice(si.original_price) || parsePrice(si.unitPrice) || parsePrice(si.unit_price) || parsePrice(si.price) || parsePrice(product?.sale_price) || parsePrice(product?.purchase_price) || 0;
      const price = parsePrice(si.price) || parsePrice(si.unitPrice) || parsePrice(si.unit_price) || originalPrice || 0;
      const quantity = parseInt(si.quantity ?? si.qty ?? 1) || 1;
      
      normalized[productId] = {
        quantity,
        ...si,
        item: nestedItem,
        price,
        originalPrice,
        gstRate: si.gstRate || nestedItem.gstRate || '0%',
        gst_rate: si.gst_rate || nestedItem.gst_rate || '0',
        taxIncluded: si.taxIncluded || nestedItem.taxIncluded || false,
        discountType: si.discountType || si.discount_type || 'rupee',
        discountValue: parseFloat(si.discountValue || si.discount_value || 0)
      };
    });
    return normalized;
  };

  // Load items from database
  useEffect(() => {
    if (isOpen) {
      // Initialize selected items from prop
      const initialMap = {};
      initialSelectedItems.forEach(si => {
        const id = si.item?.id || si.item_id || si.id;
        if (id) {
          const nestedItem = si.item || {
            id: id,
            name: si.name || si.item_name || 'N/A',
            gstRate: si.gstRate || '0%',
            gst_rate: si.gst_rate || '0',
            taxIncluded: si.taxIncluded || false,
            sale_price: si.originalPrice || 0,
            purchase_price: si.originalPrice || 0
          };
          initialMap[id] = {
            ...si,
            item: nestedItem
          };
        }
      });
      setSelectedItems(initialMap);
      loadItems(initialMap);
    }
  }, [isOpen, initialSelectedItems]);

  const loadItems = async (currentSelected) => {
    setIsLoading(true);
    const data = await itemService.getAllItems();
    const itemsList = data || [];
    setAllItems(itemsList);
    if (currentSelected) {
      setSelectedItems(normalizeSelectedItemsKeys(currentSelected, itemsList));
    }
    setIsLoading(false);
  };

  const filteredItems = useMemo(() => {
    return allItems.filter(item => {
      const type = item.item_type || item.itemType;
      const matchesTab = type === activeTab;
      const name = item.name || '';
      const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesTab && matchesSearch;
    });
  }, [allItems, activeTab, searchQuery]);

  const totalAmount = useMemo(() => {
    return Object.values(selectedItems).reduce((sum, si) => {
      const price = parsePrice(si.price);
      const qty = parseInt(si.quantity ?? si.qty ?? 1) || 1;

      const rawGst = si.gstRate || si.item?.gstRate || si.item?.gst_rate || si.gst_rate || "0";
      const gstRateString = rawGst.toString().replace('%', '').trim();
      const rate = parseFloat(gstRateString) || 0;
      const taxInc = si.taxIncluded || si.item?.taxIncluded || false;
      const itemPrice = isGstBill && taxInc ? price * (1 + rate / 100) : price;

      return sum + (itemPrice * qty);
    }, 0);
  }, [selectedItems, isGstBill]);

  const handleUpdateQty = (item, delta) => {
    const id = item.id;
    const current = selectedItems[id];
    const newQty = (current?.quantity || 0) + delta;

    if (newQty <= 0) {
      const newSelected = { ...selectedItems };
      delete newSelected[id];
      setSelectedItems(newSelected);
      return;
    }

    // Stock Validation for Sales
    if (billType === 'sale' && (item.item_type === 'product' || item.itemType === 'product') && delta > 0) {
       const stock = parseFloat(item.opening_stock || item.openingStock || 0);
       if (newQty > stock) {
          alert(t(LangKeys.item_select_not_enough_stock).replace('{availableStock}', stock));
          return;
       }
    }

    const price = billType === 'sale' ? parseFloat(item.sale_price || item.salePrice || 0) : parseFloat(item.purchase_price || item.purchasePrice || 0);
    const gstRate = current?.gstRate || item.gst_rate || item.gstRate || "0";
    const hsnCode = current?.hsnCode || item.hsn_sac_code || item.hsnSacCode || "";
    const taxIncluded = current?.taxIncluded || item.tax_included || item.taxIncluded || false;

    let basePrice = price;
    if (isGstBill && taxIncluded) {
      const rate = parseFloat(gstRate.toString().replace('%', '')) || 0;
      basePrice = price / (1 + (rate / 100));
    }

    setSelectedItems({
      ...selectedItems,
      [id]: { 
        item, 
        quantity: newQty, 
        price: current?.price || basePrice,
        originalPrice: current?.originalPrice || basePrice,
        gstRate,
        hsnCode,
        taxIncluded,
        taxAmount: current?.taxAmount || 0,
        discountValue: current?.discountValue || 0,
        discountType: current?.discountType || 'rupee'
      }
    });
  };

  const handleEditPrice = (si) => {
    setEditingItem(si);
  };

  const handleSavePrice = (updatedBillItem) => {
    const cleanGstRate = updatedBillItem.gstRate?.replace('%', '') || '0';
    setSelectedItems({
      ...selectedItems,
      [updatedBillItem.item.id]: {
        ...selectedItems[updatedBillItem.item.id],
        ...updatedBillItem,
        gst_rate: cleanGstRate,
        item: {
          ...selectedItems[updatedBillItem.item.id]?.item,
          ...updatedBillItem.item,
          gst_rate: cleanGstRate
        }
      }
    });
    setEditingItem(null);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
           <div style={styles.headerLeft}>
              <h3 style={styles.title}>{t(LangKeys.item_select_add_items_to_invoice)}</h3>
              <div style={styles.tabs}>
                 <button 
                   style={{...styles.tabBtn, ...(activeTab === 'product' ? styles.tabActive : {})}}
                   onClick={() => setActiveTab('product')}
                 >
                    {t(LangKeys.products)}
                 </button>
                 <button 
                   style={{...styles.tabBtn, ...(activeTab === 'service' ? styles.tabActive : {})}}
                   onClick={() => setActiveTab('service')}
                 >
                    {t(LangKeys.services)}
                 </button>
              </div>
           </div>
           <button style={styles.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={styles.searchBar}>
           <Search size={18} color="#94A3B8" />
           <input 
             style={styles.searchInput} 
             placeholder={t(LangKeys.item_select_search_created_items)} 
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
           />
        </div>

        <div style={styles.list}>
           {isLoading ? (
             <div style={styles.loading}>{t(LangKeys.loading || 'Loading...')}</div>
           ) : filteredItems.length === 0 ? (
             <div style={styles.empty}>{t(LangKeys.no_items_found || 'No items found')}</div>
           ) : (
             filteredItems.map(item => {
               const selected = selectedItems[item.id];
               const type = item.item_type || item.itemType;
               const stock = item.opening_stock || item.openingStock || 0;
               const salePrice = item.sale_price || item.salePrice || 0;
               const purPrice = item.purchase_price || item.purchasePrice || 0;
               const quantity = selectedItems[item.id]?.quantity || 0;

               return (
                 <div key={item.id} style={styles.itemRow}>
                    <div style={styles.itemIcon}>
                       {type === 'product' ? <Package size={20} color="#64748B" /> : <ShoppingBag size={20} color="#64748B" />}
                    </div>
                    <div style={styles.itemInfo}>
                       <span style={styles.itemName}>{item.name}</span>
                       <div style={styles.itemDetails}>
                          <span>{billType === 'sale' ? t(LangKeys.sale_price) : t(LangKeys.purchase_price)}: {formatCurrency(billType === 'sale' ? salePrice : purPrice)}</span>
                          {type === 'product' && (
                            <span style={{color: stock < 5 ? '#EF4444' : '#64748B'}}>
                               • {t(LangKeys.current_stock)}: {stock}
                            </span>
                          )}
                       </div>
                    </div>
                    
                    {selected ? (
                      <div style={styles.selectedActions}>
                         <div style={styles.qtyControl}>
                            <button style={styles.qtyBtn} onClick={() => handleUpdateQty(item, -1)}>
                               {quantity === 1 ? <Trash2 size={14} /> : <Minus size={14} />}
                            </button>
                            <span style={styles.qtyVal}>{quantity}</span>
                            <button style={styles.qtyBtn} onClick={() => handleUpdateQty(item, 1)}><Plus size={14} /></button>
                         </div>
                         <button style={styles.editBtn} onClick={() => handleEditPrice(selected)}>
                            <Edit2 size={14} />
                         </button>
                      </div>
                    ) : (
                      <button style={styles.addBtn} onClick={() => handleUpdateQty(item, 1)}>
                         {t(LangKeys.add)}
                      </button>
                    )}
                 </div>
               );
             })
           )}
        </div>

        <div style={styles.footer}>
           <div style={styles.summary}>
              <span style={styles.sumCount}>{Object.keys(selectedItems).length} {t(LangKeys.item_select_item_plural)}</span>
              <span style={styles.sumAmount}>
                {formatCurrency(totalAmount)}
              </span>
           </div>
           <button 
             style={styles.continueBtn} 
             onClick={() => onSelect(Object.values(selectedItems))}
             disabled={Object.keys(selectedItems).length === 0}
           >
              {t(LangKeys.item_select_continue)}
           </button>
        </div>

        {editingItem && (
          <EditPriceModal 
            isOpen={true}
            onClose={() => setEditingItem(null)}
            billItem={editingItem}
            isGstBill={isGstBill}
            isPurchaseBill={billType === 'purchase'}
            onSave={handleSavePrice}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2500 },
  modal: { width: '100%', maxWidth: '600px', background: 'white', borderRadius: '24px', overflow: 'hidden', height: '90vh', display: 'flex', flexDirection: 'column' },
  header: { padding: '20px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px' },
  title: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  tabs: { display: 'flex', gap: '8px' },
  tabBtn: { padding: '6px 16px', borderRadius: '20px', border: '1px solid #E2E8F0', background: 'white', fontSize: '13px', fontWeight: '700', color: '#64748B', cursor: 'pointer' },
  tabActive: { background: '#1D4ED8', color: 'white', borderColor: '#1D4ED8' },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#94A3B8' },
  searchBar: { margin: '16px 24px', background: '#F8FAFC', borderRadius: '14px', border: '1px solid #E2E8F0', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '10px', height: '44px' },
  searchInput: { flex: 1, border: 'none', background: 'transparent', outline: 'none', fontWeight: '600', fontSize: '14px' },
  list: { flex: 1, overflowY: 'auto', padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '12px' },
  itemRow: { background: 'white', border: '1px solid #F1F5F9', borderRadius: '16px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
  itemIcon: { width: '44px', height: '44px', borderRadius: '12px', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  itemInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  itemName: { fontSize: '15px', fontWeight: '800', color: '#1E293B' },
  itemDetails: { fontSize: '12px', color: '#64748B', fontWeight: '600', display: 'flex', gap: '8px' },
  addBtn: { padding: '8px 20px', borderRadius: '10px', border: '1px solid #1D4ED8', background: 'white', color: '#1D4ED8', fontWeight: '800', fontSize: '13px', cursor: 'pointer' },
  selectedActions: { display: 'flex', alignItems: 'center', gap: '10px' },
  qtyControl: { display: 'flex', alignItems: 'center', gap: '12px', background: '#F8FAFC', padding: '4px', borderRadius: '10px', border: '1px solid #E2E8F0' },
  qtyBtn: { width: '28px', height: '28px', borderRadius: '6px', border: 'none', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1D4ED8', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  qtyVal: { fontSize: '14px', fontWeight: '800', color: '#1E293B', minWidth: '20px', textAlign: 'center' },
  editBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#F1F5F9', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  footer: { padding: '20px 24px', borderTop: '1px solid #F1F5F9', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  summary: { display: 'flex', flexDirection: 'column' },
  sumCount: { fontSize: '13px', fontWeight: '700', color: '#94A3B8' },
  sumAmount: { fontSize: '18px', fontWeight: '900', color: '#1D4ED8' },
  continueBtn: { height: '48px', padding: '0 32px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', fontSize: '15px', cursor: 'pointer', transition: '0.2s' },
  loading: { padding: '40px', textAlign: 'center', color: '#94A3B8', fontWeight: '600' },
  empty: { padding: '40px', textAlign: 'center', color: '#94A3B8', fontWeight: '600' }
};

export default ItemSelectModal;
